import type { BehindText, Clip, Overlay, Segment } from "./clip.js";

export interface TimelineOverlay { overlay: Overlay; range: Segment }

/** Shorter than this and a picture has no time to be seen. */
export const MIN_OVERLAY_MS = 200;

/**
 * The one continuous stretch of the timeline a picture covers: from the first kept moment at or after its start to the last kept
 * moment at or before its end. Cuts inside that stretch are crossed, never split, so a picture can run across one piece or many.
 * Null when nothing of it is kept.
 */
export function overlaySpan(segments: Segment[], o: Pick<Overlay, "startMs" | "endMs">): Segment | null {
  let acc = 0;
  let start: number | null = null;
  let end: number | null = null;
  for (const s of segments) {
    const len = s.outMs - s.inMs;
    if (start == null && o.startMs < s.outMs) start = acc + Math.max(0, o.startMs - s.inMs);
    if (o.endMs > s.inMs) end = acc + Math.min(len, o.endMs - s.inMs);
    acc += len;
  }
  return start != null && end != null && end - start >= MIN_OVERLAY_MS ? { inMs: start, outMs: end } : null;
}

/** Whether a picture's source range touches any kept piece at all (a picture whose words were cut never shows). */
export const overlayInPieces = (segments: Segment[], o: Pick<Overlay, "startMs" | "endMs">): boolean => segments.some((s) => o.endMs > s.inMs && o.startMs < s.outMs);
/** A source range pulled into the kept pieces: trimmed to the stretch that overlaps them, or null when none does. */
export function snapToPieces(segments: Segment[], startMs: number, endMs: number): { startMs: number; endMs: number } | null {
  const hit = segments.filter((s) => endMs > s.inMs && startMs < s.outMs);
  if (!hit.length) return null;
  return { startMs: Math.max(startMs, hit[0].inMs), endMs: Math.min(endMs, hit[hit.length - 1].outMs) };
}

export interface TimelineBehind { item: BehindText; range: Segment }
/** Behind-the-speaker texts that will show (enabled, with words), each with its one timeline span (crossing cuts like a picture). */
export function timelineBehindTexts(clip: Pick<Clip, "segments" | "behindTexts">): TimelineBehind[] {
  return (clip.behindTexts ?? [])
    .filter((t) => t.enabled && t.text.trim())
    .map((t) => ({ item: t, range: overlaySpan(clip.segments, t) }))
    .filter((t): t is TimelineBehind => t.range != null);
}

/** Overlays that will actually show (enabled, with an image), each with its one timeline span. */
export function timelineOverlays(clip: Pick<Clip, "segments" | "overlays">): TimelineOverlay[] {
  return clip.overlays
    .filter((o) => o.enabled && (o.image || o.video))
    .map((o) => ({ overlay: o, range: overlaySpan(clip.segments, o) }))
    .filter((t): t is TimelineOverlay => t.range != null);
}

/** Edits from the editor and results from generation jobs, combined without losing either: the editor's overlays win, generated images are added to them,
 *  and overlays the server created after the editor last synced (the AI planner) are appended. */
export function mergeOverlays(latest: Overlay[], incoming: Overlay[], incomingStamp?: string): Overlay[] {
  const byId = new Map(latest.map((o) => [o.id, o]));
  const out = incoming.map((o) => {
    const s = byId.get(o.id);
    if (!s) return o;
    const images = [...o.images, ...s.images.filter((i) => !o.images.includes(i))];
    // videos the studio made arrive the same way as pictures: added to the editor's list; a new one goes into use, but an owner who switched back to the picture keeps that choice
    const fresh = (s.videos ?? []).filter((v) => !(o.videos ?? []).includes(v));
    const videos = [...(o.videos ?? []), ...fresh];
    return { ...o, images, image: o.image ?? s.image ?? null, videos, video: o.video ?? (fresh.length ? fresh[fresh.length - 1] : null), prompt: o.prompt || s.prompt };
  });
  const seen = new Set(incoming.map((o) => o.id));
  for (const s of latest) if (!seen.has(s.id) && (!incomingStamp || s.createdAt > incomingStamp)) out.push(s);
  return out;
}

/** A picture fades in and out over this long; a picture that shows only for a moment gets shorter fades, so both always fit inside it. */
export const OVERLAY_FADE_MS = 300;
export const overlayFadeMs = (spanMs: number): number => Math.max(1, Math.min(OVERLAY_FADE_MS, spanMs / 3));
/**
 * How visible a picture is `t` ms into a span of `spanMs`: rising through the fade in, full in the middle, falling through the fade
 * out. Works from the span the timeline gives it now, so a picture made shorter or longer still fades at both of its ends.
 */
export function overlayOpacity(t: number, spanMs: number): number {
  const f = overlayFadeMs(spanMs);
  return Math.max(0, Math.min(1, t / f, (spanMs - t) / f));
}

export const overlayId = () => `ov-${Date.now().toString(36)}${Math.floor(Math.random() * 1296).toString(36).padStart(2, "0")}`;
